import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, Plus, Loader2, Trash2, LineChart, BookOpen } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import SprayNav from '../../components/spray/SprayNav';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { TYPE_ICON } from '../../components/spray/pestModelDefaults';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import pestModelService from '../../services/pestModels';
import { errorMessage } from '../../utils/apiResponse';
import { KIND_LABEL, MODEL_TYPE_LABEL } from '../../utils/pestRisk';
import type { PestModel } from '../../types/pest';

/**
 * /spray/pests — the pest & disease list. Each pest opens on its own route
 * (/spray/pests/:id) so the header's Back and Spray buttons behave like every
 * other spray page; creating one starts at /spray/pests/new with AI research.
 */
export default function PestDiseaseAdmin() {
  const navigate = useNavigate();
  const canManage = useModuleLicense('spray', 'admin').hasAccess;

  const [models, setModels] = useState<PestModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setModels(await pestModelService.listModels()); setError(null); } catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (m: PestModel) => {
    if (!confirm(`Delete "${m.name}"? This removes its risk history and alert rules.`)) return;
    try { await pestModelService.deleteModel(m.id); await load(); } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <>
      <PageHeader
        title="Pests & Diseases"
        subtitle="Define pests, their hosts, forecasting models and alerts"
        icon={<Bug className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/weather/pests')}>
              <LineChart className="h-4 w-4" /> Risk dashboard
            </Button>
            {canManage && (
              <Button variant="primary" size="sm" onClick={() => navigate('/spray/pests/new')}>
                <Plus className="h-4 w-4" /> New pest
              </Button>
            )}
          </div>
        }
      />
      <SprayNav />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-5">
        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2 text-sm">{error}</div>}

        {loading ? (
          <div className="flex items-center gap-2 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>
        ) : models.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-neutral-500">
            <Bug className="h-8 w-8 mx-auto mb-3 text-neutral-300" />
            <p>No pests defined yet.</p>
            {canManage && <Button variant="primary" className="mt-4" onClick={() => navigate('/spray/pests/new')}><Plus className="h-4 w-4" /> Add your first pest</Button>}
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {models.map((m) => (
              <div key={m.id} className="border border-neutral-200 bg-white rounded-xl p-4 hover:border-neutral-300 transition">
                <div className="flex items-start gap-2">
                  <div className="mt-0.5 text-neutral-400">{TYPE_ICON[m.model_type]}</div>
                  <div className="min-w-0 flex-1">
                    <button onClick={() => navigate(`/spray/pests/${m.id}`)} className="text-left max-w-full">
                      <div className="font-semibold text-neutral-900 truncate">{m.name}</div>
                      {m.scientific_name && <div className="text-xs italic text-neutral-500 truncate">{m.scientific_name}</div>}
                    </button>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-600">{KIND_LABEL[m.kind]}</span>
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700">{MODEL_TYPE_LABEL[m.model_type]}</span>
                      {m.profile && <span className="text-[11px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 inline-flex items-center gap-1"><BookOpen className="h-3 w-3" /> field guide</span>}
                      {!m.is_active && <span className="text-[11px] px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-400">inactive</span>}
                    </div>
                    {m.host_crop_type_names && m.host_crop_type_names.length > 0 && (
                      <div className="text-xs text-neutral-500 mt-1.5 truncate">Hosts: {m.host_crop_type_names.join(', ')}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-neutral-100">
                  <button onClick={() => navigate(`/spray/pests/${m.id}`)} className="text-xs px-2 py-1 rounded hover:bg-neutral-100 text-neutral-600">Open</button>
                  {canManage && <button onClick={() => remove(m)} className="ml-auto p-1.5 rounded hover:bg-red-50 text-neutral-400 hover:text-red-600" aria-label={`Delete ${m.name}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
